import React from 'react';
import './Collections.css';
import { MdClose } from 'react-icons/md';
import { Availability, TimeSlot, WeeklySchedule } from '../../client/core';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS: Record<string, string> = {
    monday: 'Monday',
    tuesday: 'Tuesday',
    wednesday: 'Wednesday',
    thursday: 'Thursday',
    friday: 'Friday',
    saturday: 'Saturday',
    sunday: 'Sunday'
};

export interface WeeklyHoursProps {
    availability: Availability;
    onChange: (availability: Availability) => void;
}

const WeeklyHours: React.FC<WeeklyHoursProps> = ({
    availability,
    onChange
}) => {
    const weekly = availability?.weekly || [];

    const isDayEnabled = (day: string): boolean => {
        return weekly.some(w => w.day === day);
    };

    const getTimesForDay = (day: string): TimeSlot[] => {
        const daySchedule = weekly.find(w => w.day === day);
        return daySchedule?.times || [];
    };

    const summaryForDay = (day: string): string => {
        if (!isDayEnabled(day)) {
            return 'Closed';
        }

        const times = getTimesForDay(day);

        if (times.length === 0) {
            return 'Open all day';
        }

        return times.map(t => `${t.start}–${t.end}`).join(', ');
    };

    const allDaysEnabled = DAYS.every(isDayEnabled);

    const toggleDay = (day: string) => {
        if (isDayEnabled(day)) {
            onChange({
                ...availability,
                weekly: weekly.filter(w => w.day !== day)
            });
        } else {
            onChange({
                ...availability,
                weekly: [...weekly, { day: day as WeeklySchedule['day'], times: [] }]
            });
        }
    };

    const toggleAllDays = () => {
        if (allDaysEnabled) {
            onChange({ ...availability, weekly: [] });
            return;
        }

        onChange({
            ...availability,
            weekly: DAYS.map(day => ({
                day: day as WeeklySchedule['day'],
                times: getTimesForDay(day)
            }))
        });
    };

    const addTimeRange = (day: string) => {
        const newWeekly = weekly.map(w => {
            if (w.day === day) {
                return { ...w, times: [...w.times, { start: '09:00', end: '17:00' }] };
            }
            return w;
        });
        onChange({ ...availability, weekly: newWeekly });
    };

    const removeTimeRange = (day: string, index: number) => {
        const newWeekly = weekly.map(w => {
            if (w.day === day) {
                const newTimes = [...w.times];
                newTimes.splice(index, 1);
                return { ...w, times: newTimes };
            }
            return w;
        });
        onChange({ ...availability, weekly: newWeekly });
    };

    const updateTimeRange = (day: string, index: number, field: 'start' | 'end', value: string) => {
        const newWeekly = weekly.map(w => {
            if (w.day === day) {
                const newTimes = w.times.map((t, i) => {
                    if (i === index) {
                        return { ...t, [field]: value };
                    }
                    return t;
                });
                return { ...w, times: newTimes };
            }
            return w;
        });
        onChange({ ...availability, weekly: newWeekly });
    };

    const copyToOpenDays = (day: string) => {
        const times = getTimesForDay(day);
        onChange({
            ...availability,
            weekly: weekly.map(w => ({ ...w, times: times.map(t => ({ ...t })) }))
        });
    };

    return (
        <div className="cp-weekly">
            <div className="cp-weekly-head">
                <h2 className="cp-weekly-title">Weekly hours</h2>
                <button
                    type="button"
                    className="cp-link-button"
                    onClick={toggleAllDays}
                    data-testid="toggle-all-days"
                >
                    {allDaysEnabled ? 'Clear all days' : 'Select all days'}
                </button>
            </div>

            <p className="cp-field-hint">
                Tick the days this point is open. Leave a day without intervals and it&apos;s treated as open all day.
            </p>

            <div className="availability-editor">
                {DAYS.map(day => (
                    <div key={day} className="day-row">
                        <div className="day-header">
                            <label className="day-toggle">
                                <input
                                    type="checkbox"
                                    checked={isDayEnabled(day)}
                                    onChange={() => toggleDay(day)}
                                    data-testid={`toggle-${day}`}
                                />
                                <span className="day-label">{DAY_LABELS[day]}</span>
                                <span className="day-summary">{summaryForDay(day)}</span>
                            </label>
                        </div>

                        {isDayEnabled(day) && (
                            <div className="day-content">
                                <div className="time-ranges">
                                    {getTimesForDay(day).map((timeRange, index) => (
                                        <div key={index} className="time-range" data-testid={`time-range-${day}-${index}`}>
                                            <input
                                                type="time"
                                                value={timeRange.start}
                                                onChange={(e) => updateTimeRange(day, index, 'start', e.target.value)}
                                                data-testid={`start-time-${day}-${index}`}
                                                className="time-select"
                                                aria-label={`${DAY_LABELS[day]} start time`}
                                            />

                                            <span className="time-separator">to</span>

                                            <input
                                                type="time"
                                                value={timeRange.end}
                                                onChange={(e) => updateTimeRange(day, index, 'end', e.target.value)}
                                                data-testid={`end-time-${day}-${index}`}
                                                className="time-select"
                                                aria-label={`${DAY_LABELS[day]} end time`}
                                            />

                                            <button
                                                type="button"
                                                onClick={() => removeTimeRange(day, index)}
                                                data-testid={`delete-range-${day}-${index}`}
                                                className="delete-button"
                                                aria-label="Remove time range"
                                            >
                                                <MdClose size={18} />
                                            </button>

                                            {timeRange.start && timeRange.end && timeRange.end <= timeRange.start && (
                                                <span className="cp-interval-error" data-testid={`interval-error-${day}-${index}`}>
                                                    End time must be after the start time
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                <div className="cp-day-actions">
                                    <button
                                        type="button"
                                        onClick={() => addTimeRange(day)}
                                        data-testid={`add-interval-${day}`}
                                        className="add-interval-button"
                                    >
                                        + Add interval
                                    </button>

                                    {getTimesForDay(day).length > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => copyToOpenDays(day)}
                                            data-testid={`copy-intervals-${day}`}
                                            className="cp-copy-button"
                                        >
                                            Copy to open days
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default WeeklyHours;
